/**
 * Thalès glissant — calculs purs.
 *
 * Deux droites sécantes en A : (AB) et (AC). M est sur (AB) avec AM = k·AB en
 * vecteurs (k < 0 : de l'autre côté de A, configuration « papillon »), N sur
 * (AC) avec AN = k′·AC. En mode « Thalès », N suit M (k′ = k) : (MN) reste
 * parallèle à (BC). En mode « réciproque », N se règle seul.
 */

import type { Pt } from '../../ui/frame';

export const K_STEP = 0.05;
export const K_MIN = -1.5;
export const K_MAX = 2;

export function pointAt(p: Pt, q: Pt, k: number): Pt {
  return { x: p.x + k * (q.x - p.x), y: p.y + k * (q.y - p.y) };
}

/** Paramètre k du projeté de P sur la droite (PQ) : P + k(Q − P). */
export function projectParam(p: Pt, q: Pt, target: Pt): number {
  const dx = q.x - p.x;
  const dy = q.y - p.y;
  const n2 = dx * dx + dy * dy;
  if (n2 === 0) return 0;
  return ((target.x - p.x) * dx + (target.y - p.y) * dy) / n2;
}

/**
 * k au pas de 0,05, borné, jamais nul (M confondu avec A n'a pas de sens
 * ici). En passant par 0, on saute de l'autre côté : le sens vient de la
 * valeur précédente (curseur au clavier) ou du signe de k (au doigt).
 */
export function snapK(k: number, previous?: number): number {
  let r = Math.round(k / K_STEP) * K_STEP;
  r = Math.min(K_MAX, Math.max(K_MIN, r));
  r = Number(r.toFixed(2));
  if (r === 0) {
    const down = previous !== undefined && k !== previous ? k < previous : k < 0;
    r = down ? -K_STEP : K_STEP;
  }
  return r;
}

export function dist(p: Pt, q: Pt): number {
  return Math.hypot(q.x - p.x, q.y - p.y);
}

export interface ThalesFigure {
  A: Pt;
  B: Pt;
  C: Pt;
  k: number;
  /** paramètre de N sur (AC) en mode réciproque */
  k2: number;
}

export interface ThalesReading {
  M: Pt;
  N: Pt;
  am: number;
  ab: number;
  an: number;
  ac: number;
  mn: number;
  bc: number;
  /** AM/AB, AN/AC, MN/BC */
  rAB: number;
  rAC: number;
  rBC: number;
  /** (MN) // (BC) */
  parallel: boolean;
  /** A, M, B et A, N, C dans le même ordre (même signe de k et k′) */
  sameOrder: boolean;
  butterfly: boolean;
}

export function read(f: ThalesFigure, linked: boolean): ThalesReading {
  const k2 = linked ? f.k : f.k2;
  const M = pointAt(f.A, f.B, f.k);
  const N = pointAt(f.A, f.C, k2);
  const ab = dist(f.A, f.B);
  const ac = dist(f.A, f.C);
  const bc = dist(f.B, f.C);
  const mn = dist(M, N);
  const cross = (N.x - M.x) * (f.C.y - f.B.y) - (N.y - M.y) * (f.C.x - f.B.x);
  return {
    M,
    N,
    am: Math.abs(f.k) * ab,
    ab,
    an: Math.abs(k2) * ac,
    ac,
    mn,
    bc,
    rAB: Math.abs(f.k),
    rAC: Math.abs(k2),
    rBC: bc ? mn / bc : 0,
    parallel: Math.abs(cross) < 1e-9 * Math.max(1, bc * bc),
    sameOrder: Math.sign(f.k) === Math.sign(k2),
    butterfly: f.k < 0 && k2 < 0,
  };
}

/** A, B, C non alignés */
export function isTriangle(f: Pick<ThalesFigure, 'A' | 'B' | 'C'>): boolean {
  return (f.B.x - f.A.x) * (f.C.y - f.A.y) - (f.B.y - f.A.y) * (f.C.x - f.A.x) !== 0;
}

export interface ThalesGoal {
  id: string;
  text: string;
  /** mode demandé */
  linked: boolean;
  check: (f: ThalesFigure) => boolean;
}

export const GOALS: readonly ThalesGoal[] = [
  {
    id: 'half',
    text: 'Place M entre A et B pour que MN soit la moitié de BC.',
    linked: true,
    check: (f) => Math.abs(f.k - 0.5) < 1e-9,
  },
  {
    id: 'butterfly',
    text: 'Configuration papillon avec MN = 0,75 × BC.',
    linked: true,
    check: (f) => Math.abs(f.k + 0.75) < 1e-9,
  },
  {
    id: 'converse',
    text: 'Mode réciproque : place N pour que (MN) soit parallèle à (BC).',
    linked: false,
    check: (f) => Math.abs(f.k - f.k2) < 1e-9,
  },
];
